import { type AxiosError } from 'axios';

import { getEnvironment, IsAnyTLDEnvironment } from './environment';
import { getConfig } from '../config/appConfig';

export interface URLSchema {
    api: string;
    websocket: string;
    vanityUrl: string;
    webGlobal: string;
}

export const parseUrlState = (state): any => {
    let params = {};

    if (!state || typeof state !== 'string') {
        return params;
    }

    try {
        params = JSON.parse(state);
    } catch (error) {
        try {
            params = JSON.parse(decodeURIComponent(state));
        } catch (err) {
            state.split('/').forEach((stateParam) => {
                const [name, value] = stateParam.split(':');
                params[name] = value;
            });
        }
    }

    return params;
};

/**
 * Get all parameters from the URL query.
 */
export const getUrlParams = (): Record<string, string | undefined> => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchParamsObj = Object.fromEntries(searchParams.entries());

    return searchParamsObj;
};

/**
 * Get a parameter from the URL query.
 */
export function getUrlParam(name: string, required: true): string;
export function getUrlParam(name: string, required?: boolean): string | undefined;
export function getUrlParam(name: string, required: boolean = false): string | undefined {
    const params = getUrlParams();

    if (required && !params[name]) {
        throw new Error(`Missing required URL parameter: ${name}`);
    }

    return params[name];
}

const SAFE_URL_PROTOCOLS = ['http:', 'https:'];

/**
 * Parse an untrusted URL — one taken from a query parameter, an API payload or
 * any other caller-controlled source — and return it only when it is an
 * absolute `http`/`https` URL.
 *
 * Returns `undefined` for everything else, including relative URLs and the
 * script-bearing schemes (`javascript:`, `data:`, `vbscript:`, `blob:`). The
 * `URL` constructor normalises case and strips the leading whitespace and
 * control characters that defeat naive prefix matching, so `JavaScript:` and
 * `\u0001javascript:` are rejected along with the plain form.
 */
export const parseHttpUrl = (value: string | undefined | null): URL | undefined => {
    if (!value) {
        return undefined;
    }

    try {
        const parsed = new URL(value.trim());

        return SAFE_URL_PROTOCOLS.includes(parsed.protocol) ? parsed : undefined;
    } catch {
        return undefined;
    }
};

/**
 * Normalised `http`/`https` form of an untrusted URL, or `undefined` if it is
 * not one. Use before handing a caller-controlled URL to a navigation or embed
 * sink such as `window.location.replace` or an iframe `src`.
 */
export const sanitizeHttpUrl = (value: string | undefined | null): string | undefined => parseHttpUrl(value)?.href;

/**
 * Set all parameters in the URL query.
 */
export const setUrlParams = (searchParams: URLSearchParams) => {
    window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}`);
};

/**
 * Get a parameter from the URL query and immediately remove it from the URL for security reasons.
 */
export const popUrlParam = (name: string): string | undefined => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchParam = getUrlParam(name);

    if (searchParam === undefined) {
        return;
    }

    searchParams.delete(name);
    setUrlParams(searchParams);

    return searchParam;
};

export const isDevelopmentEnv = () => {
    const { region, environment } = getEnvironment();
    return region === 'local' || environment === 'dev';
};

export const getUrls = (): URLSchema => {
    const config = getConfig();
    if (config?.api && config?.websocket && config?.web && config?.podRegion && config?.environment) {
        const parts = config.api.split(`${config.podRegion}.${config.environment}.`);
        const domain = parts[parts.length - 1] ?? 'class.com';
        return {
            api: config.api,
            websocket: config.websocket,
            vanityUrl: config.web,
            webGlobal: `https://main.web.global.${config.environment}.${domain}`
        };
    }
    const { account, pod, region, environment } = getEnvironment();

    let websocket: string;
    let vanityUrl: string;
    let suffix: string;

    if (region === 'local') {
        suffix = `${pod}.${region}.dev.${process.env.REACT_APP_DOMAIN_NAME}`;
        websocket = `wss://websockets.${pod}.${region}.${environment}.${process.env.REACT_APP_DOMAIN_NAME}`;
        websocket = `wss://websockets.pod-1.us-east-1.test.class.com`;
        vanityUrl = `https://${account}.web.${suffix}`;
    } else {
        suffix = `${pod}.${region}.${environment}.${process.env.REACT_APP_DOMAIN_NAME}`;
        websocket = `https://websockets.${suffix}`;
        vanityUrl = IsAnyTLDEnvironment()
            ? `https://${account}.${process.env.REACT_APP_DOMAIN_NAME}`
            : `https://${account}.${environment}.${process.env.REACT_APP_DOMAIN_NAME}`;
    }

    const apiPath = process.env.REACT_APP_API_PATH || `https://${account}.rest.${suffix}`;

    return {
        api: apiPath,
        websocket,
        vanityUrl,
        webGlobal: `https://main.web.global.${environment}.${process.env.REACT_APP_DOMAIN_NAME}`
    };
};

/**
 * Process an apiPath returning the full url and optionally interpolating key values in the path.
 */
export const processApiPath = (
    apiPath: string,
    keyValues?: Record<string, string>,
    queryParams?: Record<string, string>
): string => {
    const { api } = getUrls();
    let apiPathProcessed = apiPath;
    if (keyValues) {
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, val] of Object.entries(keyValues)) {
            const value = encodeURIComponent(val);
            const pathComp = `{${key}}`;
            if (apiPath.indexOf(pathComp) === -1) {
                throw Error(`API path does not contain component key: ${pathComp}`);
            }
            apiPathProcessed = apiPathProcessed.replace(pathComp, value);
        }
    }

    const qs = queryParams ? `?${new URLSearchParams(queryParams)}` : '';
    return `${api}${apiPathProcessed}${qs}`;
};

/**
 * Builds a URL on the API host. Values go through `URLSearchParams`, so a JSON state containing `&`, `+` or `#`
 * stays one intact query parameter and can never change where the URL points.
 */
export const buildApiUrl = (apiPath: string, queryParams: Record<string, string> = {}): URL => {
    const url = new URL(`${getUrls().api}${apiPath}`);
    Object.entries(queryParams).forEach(([key, value]) => url.searchParams.set(key, value));
    return url;
};

enum ReponseError {
    ERR_NO_RESPONSE = 1000,
    ERR_REQUEST = 1001
}

export const getErrorCode = (err: AxiosError): number => {
    if (err.response) {
        return err.response.status;
    }

    if (err.request) {
        return ReponseError.ERR_NO_RESPONSE;
    }

    return ReponseError.ERR_REQUEST;
};

export const joinPath = (a: string, b: string): string =>
    `${a.endsWith('/') ? a.substring(0, a.length - 1) : a}/${b.startsWith('/') ? b.substring(1) : b}`;
